/**
 * Entries somebody writes by hand.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Sales, purchases and expenses reach the books on their own. Everything else
 * does not — the balances a business started with, depreciation, an owner
 * putting money in, a correction to last month. The balance sheet shows the
 * gap plainly: cash on hand reads negative because expenses have been paid
 * out of it and no opening balance was ever posted.
 *
 * WHAT THE SCREEN IS BUILT AROUND
 * -------------------------------
 * The running difference. Somebody entering a voucher is watching one number:
 * whether it balances yet. So that number is the largest thing on the form,
 * updates on every keystroke, and says which side is short — rather than
 * being discovered on submit, after the person has stopped looking.
 *
 * And the difference between a draft and a posted entry is made plain,
 * because it is the difference between something that can be changed and
 * something that can only be reversed.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

const BLANK_LINE = { accountCode: '', debit: '', credit: '' };

export default function VouchersPage() {
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['vouchers', status],
    queryFn: () => api.get(withQuery('/accounts/vouchers', { status })),
  });

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Vouchers</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            Entries written by hand — opening balances, adjustments, corrections.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">Showing</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
            >
              <option value="">All</option>
              <option value="draft">Awaiting approval</option>
              <option value="posted">In the books</option>
              <option value="reversed">Reversed</option>
            </select>
          </label>

          <Button onClick={() => setWriting((open) => !open)}>
            {writing ? 'Close' : 'Write a voucher'}
          </Button>
        </div>
      </div>

      {writing && <ComposeForm onDone={() => setWriting(false)} />}

      {data?.awaitingApproval > 0 && status !== 'draft' && (
        <button
          type="button"
          onClick={() => setStatus('draft')}
          className="mb-4 w-full rounded-lg bg-warn-bg px-3.5 py-2.5 text-left text-sm text-warn hover:brightness-110"
        >
          {data.awaitingApproval} voucher{data.awaitingApproval === 1 ? '' : 's'} awaiting approval
          — not in the books until somebody agrees to them.
        </button>
      )}

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : isError || !data ? (
        <p className="py-16 text-center text-sm text-ink-2">
          Could not load these. Check the branch computer is running.
        </p>
      ) : data.vouchers.length === 0 ? (
        <p className="py-16 text-center text-sm text-ink-2">Nothing here yet.</p>
      ) : (
        <div className="space-y-2">
          {data.vouchers.map((voucher) => (
            <VoucherRow key={voucher.id} voucher={voucher} />
          ))}
        </div>
      )}
    </div>
  );
}

function statusOf(voucher) {
  if (voucher.is_reversed) return { label: 'Reversed', tone: 'text-ink-3 border-rule' };
  if (voucher.reversal_of) return { label: 'A reversal', tone: 'text-ink-2 border-rule' };
  if (!voucher.is_posted) return { label: 'Draft', tone: 'text-warn border-warn' };
  return { label: 'In the books', tone: 'text-brand-400 border-brand-400' };
}

function VoucherRow({ voucher }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const state = statusOf(voucher);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['vouchers'] });

  const { data: detail } = useQuery({
    queryKey: ['vouchers', voucher.id],
    queryFn: () => api.get(`/accounts/vouchers/${voucher.id}`),
    enabled: open,
  });

  const post = useMutation({
    mutationFn: () => api.post(`/accounts/vouchers/${voucher.id}/post`),
    onSuccess: refresh,
  });

  const reverse = useMutation({
    mutationFn: (reason) => api.post(`/accounts/vouchers/${voucher.id}/reverse`, { reason }),
    onSuccess: refresh,
  });

  const discard = useMutation({
    mutationFn: () => api.delete(`/accounts/vouchers/${voucher.id}`),
    onSuccess: refresh,
  });

  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);

  return (
    <div className="rounded-xl border border-rule bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <button type="button" onClick={() => setOpen((was) => !was)} className="min-w-0 text-left">
          <span className="mr-2 text-xs tabular-nums text-ink-3">{voucher.entry_date}</span>
          <span className="text-sm text-ink">{voucher.description}</span>
          <span className="ml-2 text-xs tabular-nums text-ink-3">{voucher.entry_number}</span>
        </button>

        <div className="flex items-center gap-3">
          <span className={`rounded border px-2 py-0.5 text-xs ${state.tone}`}>{state.label}</span>
          <span className="text-sm tabular-nums text-ink">{formatPKR(voucher.total_debit)}</span>
        </div>
      </div>

      {open && (
        <div className="mt-3 border-t border-rule pt-3">
          {!detail ? (
            <p className="text-sm text-ink-3">Loading…</p>
          ) : (
            <>
              <dl className="mb-3">
                {detail.lines.map((line) => (
                  <div key={line.id} className="flex items-baseline gap-3 py-0.5">
                    <dt className="min-w-0 flex-1 text-sm text-ink-2">
                      <span className="mr-2 text-xs tabular-nums text-ink-3">
                        {line.account_code}
                      </span>
                      {line.account_name}
                    </dt>
                    {/* Two columns, as a ledger is written. A single signed
                        column would be shorter and unreadable to anybody who
                        keeps books. */}
                    <dd className="w-28 shrink-0 text-right text-sm tabular-nums text-ink">
                      {line.debit > 0 ? formatPKR(line.debit) : ''}
                    </dd>
                    <dd className="w-28 shrink-0 text-right text-sm tabular-nums text-ink">
                      {line.credit > 0 ? formatPKR(line.credit) : ''}
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="mb-3 text-xs text-ink-3">
                Written by {detail.voucher.created_by_name ?? 'somebody'}
                {detail.voucher.approved_by_name &&
                  `, approved by ${detail.voucher.approved_by_name}`}
                {detail.voucher.reversed_by_number &&
                  ` · reversed by ${detail.voucher.reversed_by_number}`}
                {detail.voucher.reversal_of_number &&
                  ` · reverses ${detail.voucher.reversal_of_number}`}
              </p>

              {!voucher.is_posted && (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => post.mutate()} disabled={post.isPending}>
                    Approve and post
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => discard.mutate()}
                    disabled={discard.isPending}
                  >
                    Discard
                  </Button>
                </div>
              )}

              {voucher.is_posted && !voucher.is_reversed && !reversing && (
                <Button variant="secondary" onClick={() => setReversing(true)}>
                  Reverse
                </Button>
              )}

              {reversing && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="min-w-48 flex-1">
                    <span className="mb-1.5 block text-xs text-ink-3">
                      Why is it being reversed? The books have to explain it.
                    </span>
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
                    />
                  </label>
                  <Button
                    onClick={() => reverse.mutate(reason)}
                    disabled={!reason.trim() || reverse.isPending}
                  >
                    Reverse it
                  </Button>
                  <Button variant="secondary" onClick={() => setReversing(false)}>
                    Cancel
                  </Button>
                </div>
              )}

              {/* Said here rather than only refusing, because "you cannot
                  delete this" without "here is what you do instead" reads as
                  a fault in the system. */}
              {voucher.is_posted && (
                <p className="mt-2 text-xs text-ink-3">
                  A voucher in the books is never deleted. Reversing it writes a second entry the
                  other way round and keeps both on the record.
                </p>
              )}

              {[post, reverse, discard].map(
                (m, i) =>
                  m.isError && (
                    <p key={i} className="mt-2 text-sm text-bad">
                      {m.error?.message ?? 'That did not work.'}
                    </p>
                  )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Writing one. */
function ComposeForm({ onDone }) {
  const queryClient = useQueryClient();

  const [kind, setKind] = useState('manual');
  const [description, setDescription] = useState('');
  const [entryDate, setEntryDate] = useState(today);
  const [lines, setLines] = useState([{ ...BLANK_LINE }, { ...BLANK_LINE }]);

  const { data: options } = useQuery({
    queryKey: ['vouchers', 'accounts'],
    queryFn: () => api.get('/accounts/vouchers/accounts'),
  });

  const totals = lines.reduce(
    (sum, line) => ({
      debit: sum.debit + (Number(line.debit) || 0),
      credit: sum.credit + (Number(line.credit) || 0),
    }),
    { debit: 0, credit: 0 }
  );

  const difference = Math.round((totals.debit - totals.credit) * 100) / 100;
  const balanced = difference === 0 && totals.debit > 0;

  const save = useMutation({
    mutationFn: (post) =>
      api.post('/accounts/vouchers', {
        kind,
        description,
        entryDate,
        post,
        lines: lines
          .filter((line) => line.accountCode)
          .map((line) => ({
            accountCode: line.accountCode,
            debit: Number(line.debit) || 0,
            credit: Number(line.credit) || 0,
          })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      onDone();
    },
  });

  const setLine = (index, field, value) =>
    setLines((was) =>
      was.map((line, i) =>
        i !== index
          ? line
          : // A line is a debit or a credit, never both — so typing in one
            // column empties the other rather than letting an impossible
            // line be built and refused later.
            field === 'debit'
            ? { ...line, debit: value, credit: '' }
            : field === 'credit'
              ? { ...line, credit: value, debit: '' }
              : { ...line, [field]: value }
      )
    );

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <label>
          <span className="mb-1.5 block text-xs text-ink-3">What kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
          >
            {Object.entries(options?.kinds ?? { manual: 'Journal voucher' }).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">What it is for</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Opening balance for cash on hand"
            className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
          />
        </label>
      </div>

      <label className="mt-3 block sm:w-48">
        <span className="mb-1.5 block text-xs text-ink-3">Dated</span>
        <input
          type="date"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
          className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
        />
      </label>

      <div className="mt-4">
        <div className="flex gap-3 pb-1 text-xs text-ink-3">
          <span className="flex-1">Account</span>
          <span className="w-28 text-right">Debit</span>
          <span className="w-28 text-right">Credit</span>
          <span className="w-8" />
        </div>

        {lines.map((line, index) => (
          <div key={index} className="flex items-center gap-3 py-1">
            <select
              value={line.accountCode}
              onChange={(e) => setLine(index, 'accountCode', e.target.value)}
              className="h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
              aria-label={`Account for line ${index + 1}`}
            >
              <option value="">Choose an account</option>
              {(options?.accounts ?? []).map((account) => (
                <option key={account.id} value={account.code}>
                  {account.code} — {account.name}
                </option>
              ))}
            </select>

            <input
              type="number"
              step="0.01"
              min="0"
              value={line.debit}
              onChange={(e) => setLine(index, 'debit', e.target.value)}
              aria-label={`Debit for line ${index + 1}`}
              className="h-11 w-28 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={line.credit}
              onChange={(e) => setLine(index, 'credit', e.target.value)}
              aria-label={`Credit for line ${index + 1}`}
              className="h-11 w-28 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none"
            />

            <button
              type="button"
              onClick={() => setLines((was) => was.filter((_, i) => i !== index))}
              disabled={lines.length <= 2}
              aria-label={`Remove line ${index + 1}`}
              className="h-11 w-8 rounded text-ink-3 hover:text-ink disabled:opacity-30"
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setLines((was) => [...was, { ...BLANK_LINE }])}
          className="mt-1 text-sm text-ink-2 hover:text-ink"
        >
          Add a line
        </button>
      </div>

      {/*
        The number the person is actually watching. Large, and it says which
        side is short rather than only that something is — being told "out by
        1,000" without being told which way is half an answer.
      */}
      <div
        className={`mt-4 flex items-center justify-between rounded-lg border-t-2 px-3 py-3 ${
          balanced ? 'border-brand-400 bg-surface-3' : 'border-warn bg-warn-bg'
        }`}
      >
        <div className="text-sm">
          <span className="text-ink-2">Debits {formatPKR(totals.debit)}</span>
          <span className="mx-2 text-ink-3">·</span>
          <span className="text-ink-2">Credits {formatPKR(totals.credit)}</span>
        </div>

        <span
          className={`text-lg font-semibold tabular-nums ${balanced ? 'text-ink' : 'text-warn'}`}
        >
          {balanced
            ? 'Balances'
            : difference === 0
              ? 'Nothing entered yet'
              : `${formatPKR(Math.abs(difference))} more ${difference > 0 ? 'credit' : 'debit'} needed`}
        </span>
      </div>

      {save.isError && (
        <p className="mt-2 text-sm text-bad">{save.error?.message ?? 'That did not work.'}</p>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={() => save.mutate(false)}
          disabled={!balanced || !description.trim() || save.isPending}
        >
          Save as draft
        </Button>
        <Button
          onClick={() => save.mutate(true)}
          disabled={!balanced || !description.trim() || save.isPending}
        >
          Approve and post
        </Button>
      </div>
    </div>
  );
}
